/**
 * Copyright ©2026 Drivepoint
 */

import {EventBus, State} from "@bainbridge-growth/node-frontend";
import NotFoundError from "../errors/NotFoundError.ts";
import ModelSettings from "./microsoft/ModelSettings.ts";
import WebAppServerClient from "./WebAppServerClient.ts";

export default class PlanService {

  public static NOT_SMARTMODEL_ID = "not_smartmodel";

  private static _plan: any;

  static async start() {
    PlanService.plan = {};
    State.registerMany("company", PlanService._onEvent);
    EventBus.registerMany("plan:changed", PlanService._onEvent);
  }

  public static async startDWHSync(): Promise<void> {
    const company = State.get("company");
    if (!company) { throw new NotFoundError(); }
    if (!PlanService.isValidPlan) { throw new NotFoundError(); }
    await WebAppServerClient.request(`/api/company/${company.id}/plan/${PlanService.plan.id}/sync`, "PATCH");
  }

  public static async updateModelStartDate(ModelStartDate: string): Promise<void> {
    const company = State.get("company");
    if (!company) { throw new NotFoundError(); }
    const settings = await ModelSettings.get();
    if (!settings) { throw new NotFoundError(); }
    try {
      await WebAppServerClient.request(`/api/company/${company.id}/plan/${settings.sharepointItemId}/modelSettings`, "PUT", {ModelStartDate});
    } catch (error) {
      logger.error(error.message);
      EventBus.dispatch({type: "addin:notification:error", message: "Failed to update plan settings", plan_id: settings.sharepointItemId, refresh: () => { PlanService.loadPlan(); }} as any);
    }
  }

  private static LOAD_PLAN_MAX_RETRIES = 2;
  private static LOAD_PLAN_RETRY_DELAY_MS = 2000;

  public static async loadPlan(): Promise<void> {
    const company = State.get("company");
    if (!company) {
      logger.warn("[PlanService] No company set, marking as not_smartmodel", {company});
      PlanService.plan = {id: PlanService.NOT_SMARTMODEL_ID, reason: "No company found. The user may not be associated with a company."};
      return;
    }
    const settings = await ModelSettings.get();
    if (!settings || !settings.sharepointItemId) {
      logger.warn("[PlanService] ModelSettings missing or no sharepointItemId", {companyId: company.id, settings});
      PlanService.plan = {
        id: PlanService.NOT_SMARTMODEL_ID,
        reason: `ModelSettings could not be read from the Settings worksheet. sharepointItemId=${settings?.sharepointItemId ?? "undefined"}`,
        diagnostics: {companyId: company.id, settings}
      };
      return;
    }
    let lastError: any;
    for (let attempt = 0; attempt <= PlanService.LOAD_PLAN_MAX_RETRIES; attempt++) {
      try {
        logger.debug("[PlanService] Loading plan", {companyId: company.id, sharepointItemId: settings.sharepointItemId, attempt: attempt + 1});
        const result = await WebAppServerClient.request(`/api/company/${company.id}/plan/${settings.sharepointItemId}`, "GET");
        if (!result) {
          logger.warn("[PlanService] API returned null/empty for plan", {companyId: company.id, sharepointItemId: settings.sharepointItemId});
          PlanService.plan = {
            id: PlanService.NOT_SMARTMODEL_ID,
            reason: `API returned no plan for company=${company.id}, itemId=${settings.sharepointItemId}`,
            diagnostics: {companyId: company.id, sharepointItemId: settings.sharepointItemId}
          };
          return;
        }
        PlanService.plan = result;
        return;
      } catch (error: any) {
        lastError = error;
        logger.error(`[PlanService] loadPlan attempt ${attempt + 1} failed`, {companyId: company.id, sharepointItemId: settings.sharepointItemId, error: error.message, status: error.status});
        if (attempt < PlanService.LOAD_PLAN_MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, PlanService.LOAD_PLAN_RETRY_DELAY_MS));
        }
      }
    }
    const attempts = PlanService.LOAD_PLAN_MAX_RETRIES + 1;
    PlanService.plan = {
      id: PlanService.NOT_SMARTMODEL_ID,
      error: lastError,
      reason: `API error: ${lastError.message}${lastError.status ? ` (HTTP ${lastError.status})` : ""} (after ${attempts} attempts)`,
      diagnostics: {companyId: company.id, sharepointItemId: settings.sharepointItemId, errorMessage: lastError.message, errorStatus: lastError.status, attempts}
    };
  }

  public static async comparePlans(planIds: string[]): Promise<void> {
    const company = State.get("company");
    if (!company) { throw new NotFoundError(); }
    const settings = await ModelSettings.get();
    if (!settings) { throw new NotFoundError(); }
    await WebAppServerClient.request(`/api/company/${company.id}/plan/${settings.sharepointItemId}/createVarianceSheets`, "POST", {source_ids: planIds});
  }

  public static set plan(value: any) {
    PlanService._plan = value;
    State.set("plan", PlanService._plan);
  }

  public static get plan(): any {
    return PlanService._plan;
  }

  public static get isValidPlan(): boolean {
    return PlanService.plan?.id && PlanService.plan?.id !== PlanService.NOT_SMARTMODEL_ID;
  }

  private static async _onEvent(event: any): Promise<void> {
    switch (event.type) {
      case "state:company":
        await PlanService.loadPlan();
        break;
      case "plan:changed":
        if (event.value?.id === PlanService.plan?.id) { await PlanService.loadPlan(); }
        break;
    }
  }

}
